/* eslint-disable @typescript-eslint/no-explicit-any */
import { GET, POST, PUT } from '@/shared/api/requests';
import { DashboardData, UserClientProjectsData, Client, Project, ProjectMapData, ProjectTeamData, ProjectUser, InvitedUser, User, AnalyticsData } from '@/types/base';

// Dashboard API
export const getDashboard = async (clientName: string): Promise<DashboardData> => {
  return (await GET(`/base/dashboard/${clientName}/`)).data;
};

// Clients API
export const getClientsByRegion = async (region: 'Denver' | 'Austin' = 'Denver'): Promise<{ clients: Client[] }> => {
  const response = await GET(`/base/clients/?region=${region}`);
  // With pagination disabled, the API returns an array directly
  // Wrap it in an object for consistency with the expected format
  return { clients: response.data };
};

// Projects API
export const getAllProjects = async (params?: { 
  search?: string; 
  status?: 'active' | 'completed' 
}): Promise<Project[]> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);
  
  const url = `/base/projects/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await GET(url);
  
  // With pagination disabled on the backend, this should return an array directly
  return response.data;
};

export const getUserClientProjects = async (): Promise<UserClientProjectsData> => {
  return (await GET('/base/user/client-projects/')).data;
};

export const getProjectMap = async (): Promise<ProjectMapData> => {
  return (await GET('/base/projects/map/')).data;
};

export const getProjectClients = async (projectId: number): Promise<{ clients: Client[] }> => {
  return (await GET(`/base/projects/${projectId}/clients/`)).data;
};

// Project Team Management API
export const getProjectTeam = async (
  clientName: string, 
  projectName: string
): Promise<ProjectTeamData> => {
  return (await GET(`/base/projects/${clientName}/${projectName}/team/`)).data;
};

export const addUserToProject = async (data: {
  project_id: number;
  user_id: number;
  client_name?: string;
  role?: string;
  can_access_asbuilds?: boolean;
  can_access_progress?: boolean;
  can_access_drones?: boolean;
  can_access_drone_photos?: boolean;
  can_access_surveillance?: boolean;
}): Promise<ProjectUser> => {
  return (await POST('/base/projects/add-user/', data)).data;
};

export const inviteUserToProject = async (data: {
  project_id: number;
  external_email: string;
  external_company_name?: string;
  client_name?: string;
  role?: string;
  can_access_asbuilds?: boolean;
  can_access_progress?: boolean;
  can_access_drones?: boolean;
  can_access_drone_photos?: boolean;
  can_access_surveillance?: boolean;
}): Promise<InvitedUser> => {
  return (await POST('/base/projects/invite-user/', data)).data;
};

export const deleteUsersFromProject = async (data: {
  project_id: number;
  records: Array<{
    type: 'project_user' | 'invited_user';
    id: number;
  }>;
}): Promise<{ message: string; deleted_count: number; warnings?: string[] }> => {
  return (await POST('/base/projects/delete-users/', data)).data;
};

// Invited Users API
export const updateInvitedUser = async (
  userId: number, 
  data: Partial<InvitedUser>
): Promise<InvitedUser> => {
  return (await PUT(`/base/invited-users/${userId}/`, data)).data;
};

export const getInvitationDetails = async (token: string): Promise<{ invited_user: InvitedUser }> => {
  return (await GET(`/base/accept-invitation/?token=${token}`)).data;
};

export const acceptInvitation = async (token: string): Promise<{ 
  message: string; 
  invited_user: InvitedUser 
}> => {
  return (await POST('/base/accept-invitation/', { token })).data;
};

// Users by Client API
export const getClientUsers = async (clientId: number): Promise<{ users: User[] }> => {
  return (await GET(`/base/clients/${clientId}/users/`)).data;
};

// Analytics API
export const getStaffAnalytics = async (): Promise<AnalyticsData> => {
  return (await GET('/base/analytics/staff/')).data;
};

// Internal User Invitation (based on your Django view)
export const inviteInternalUser = async (data: {
  project_id: number;
  email: string;
  role: string;
}): Promise<{ success: boolean; error?: string }> => {
  // Note: This appears to be a legacy endpoint that returns a different format
  // You may want to create a new API endpoint that follows REST conventions
  return (await POST('/base/invite-internal-user/', data)).data;
};

// Utility functions for handling form data
export const createInviteUserFormData = (data: {
  project_id: number;
  external_email: string;
  external_company_name?: string;
  role?: string;
  can_access_asbuilds?: boolean;
  can_access_progress?: boolean;
  can_access_drones?: boolean;
  can_access_drone_photos?: boolean;
  can_access_surveillance?: boolean;
}): FormData => {
  const formData = new FormData();
  
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Convert boolean values to strings for FormData
      const stringValue = typeof value === 'boolean' ? value.toString() : value.toString();
      formData.append(key, stringValue);
    }
  });
  
  return formData;
};

// Helper function to handle legacy endpoints that expect FormData
export const inviteUserToProjectLegacy = async (data: {
  project_id: number;
  external_email: string;
  external_company_name?: string;
  client_name?: string;
  role?: string;
  can_access_asbuilds?: boolean;
  can_access_progress?: boolean;
  can_access_drones?: boolean;
  can_access_drone_photos?: boolean;
  can_access_surveillance?: boolean;
}): Promise<{ success: boolean; error?: string }> => {
  const formData = createInviteUserFormData(data);
  return (await POST('/base/invite-user-to-project/', formData)).data;
};

